import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostListener,
    computed,
    input,
    model,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    inject,
    viewChild
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Auth, PDF_WORKER_SRC } from '@trade-platform/ui-shared';
import {
    AixImageViewerComponent,
    AixLoadingComponent,
    AixNotificationComponent,
    AixPdfViewerComponent,
    debounceListener
} from '@trade-platform/ui-components';
import { ENVIRONMENT, getFromStorage, IEnvironment, pdfName } from '@trade-platform/ui-utils';
import {
    RenderedSignatureField,
    SignatureField,
    SignaturesPreviewResponse,
    getSignatureGroupLabels
} from '@trade-platform/ui-shared';

export type PendingSignatureNavigation = SignatureField | null;
// @ts-ignore: legacy build has no type declarations
import * as PDFJS from 'pdfjs-dist/legacy/build/pdf.mjs';
import { merge, Observable, Subject, Subscription } from 'rxjs';
import {
    AixAuthService,
    isAllowedDocusignRedirectUrl,
    routeConstants
} from '@advisor-ui/app-services';
import { getFormByIndex } from '../../process/overview/utils/order-utils';
import { getDocumentIndexById, getFileRouteId } from '../../utils';
import { AsyncPipe, NgClass, NgStyle } from '@angular/common';
import { LetDirective } from '@ngrx/component';
import { RemoteDataModule } from 'ngx-remotedata';
import { SecurePipe } from '@advisor-ui/app-components';
import { BaseOrdersStoreFacade, ORDERS_STORE_FACADE } from '@advisor-ui/orders';
import { getSignatureFieldsForDocument } from './signature-fields.helper';

/** #5580 — shown when the signing URL is one we will not open. */
export const SIGN_REDIRECT_BLOCKED_MESSAGE =
    'We could not open the signing session. Please contact support if this continues.';

type PDFSource = {
    url: string;
    httpHeaders?: Record<string, any>;
};

@Component({
    selector: 'aix-document-viewer',
    templateUrl: './document-viewer.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        NgStyle,
        AixLoadingComponent,
        AixNotificationComponent,
        AixPdfViewerComponent,
        LetDirective,
        AixImageViewerComponent,
        NgClass,
        AsyncPipe,
        RemoteDataModule,
        SecurePipe
    ]
})
export class AixDocumentViewerComponent implements OnChanges, OnDestroy {
    private environment = inject<IEnvironment>(ENVIRONMENT);
    router = inject(Router);
    route = inject(ActivatedRoute);
    storeFacade = inject<BaseOrdersStoreFacade>(ORDERS_STORE_FACADE);
    private ref = inject(ChangeDetectorRef);
    private authService = inject(AixAuthService);

    currentDocument = model<number>(0);
    documentControls = input<any>({});
    signaturesPreview = input<SignaturesPreviewResponse | null>(null);
    hiddenRoles = input<Set<string>>(new Set());
    wetSignOverride = input<boolean>(false);

    readonly pdf = viewChild(AixPdfViewerComponent);

    readonly image = viewChild(AixImageViewerComponent);

    currentPage = 0;

    actionsLoadingState$: Observable<any>;

    authToken: string;

    styles = {
        pdfWrapper: {}
    };

    PDFJSloaded = false;
    offline = false;

    loadingImage = new Subject();
    isPdf = true;
    isSpreadsheet = false;
    pdfLoaded = false;
    pdfLoadError = false;
    pdfFullscreen = false;
    pdfName: (name: string) => string;
    pendingSignatureNavigation: PendingSignatureNavigation = null;

    subscriptions: Subscription[] = [];

    readonly staticStyles = {
        pdfFullScreenStyle: {
            position: 'absolute',
            left: '0',
            width: '100%',
            'z-index': '1002'
        },
        pdfInnerFullscreenStyle: {
            width: '100%'
        }
    };

    form: UntypedFormGroup;

    pdfSource: PDFSource;

    get currentDocumentId(): string {
        // Signature fields use formId as their documentId, not the file id
        return this.storeFacade.documentViewer.currentFile?.formId || '';
    }

    private _allSignatureFields = computed<RenderedSignatureField[]>(() => {
        const preview = this.signaturesPreview();
        if (!preview) return [];
        return Object.entries(preview).flatMap(([key, g]) =>
            g.signatures.map(s => ({ ...s, groupKey: key }))
        );
    });

    cachedSignatureFields: RenderedSignatureField[] = [];

    private updateCurrentSignatureFields() {
        if (!this.pdfLoaded) {
            this.cachedSignatureFields = [];
            return;
        }
        const docId = this.currentDocumentId;
        const all = this._allSignatureFields();
        this.cachedSignatureFields = getSignatureFieldsForDocument(all, docId);
    }

    wetSignRoles = computed(() => {
        const preview = this.signaturesPreview();
        if (!preview) return new Set<string>();
        const override = this.wetSignOverride();
        return new Set(
            Object.entries(preview)
                .filter(([, group]) => (override || group.wetSign) && group.signatures.length > 0)
                .map(([key]) => key)
        );
    });

    // groupKey -> display label, shared with the sidebar so overlay labels never disagree with it.
    roleLabels = computed(() => {
        const preview = this.signaturesPreview();
        if (!preview) return new Map<string, string>();
        const labels = new Map<string, string>();
        for (const [key, { displayName }] of getSignatureGroupLabels(preview)) {
            labels.set(key, displayName);
        }
        return labels;
    });

    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);

    constructor() {
        this.authToken = 'Bearer ' + (getFromStorage('auth') as Auth).accessToken;

        this.pdfName = pdfName;

        this.form = new UntypedFormGroup({
            recipient: new UntypedFormControl()
        });

        this.actionsLoadingState$ = merge(
            this.storeFacade.orderApproveRemoteData$,
            this.storeFacade.orderSignRemoteData$,
            this.storeFacade.orderMarkNigoRemoteData$
        );

        this.storeFacade.actions.documentViewerSetTab.dispatch({ tab: 'Order' });

        Promise.all([
            // @ts-ignore: only accepted in commonJs o esNext
            import(/* webpackChunkName: "PDFJS" */ 'pdfjs-dist/legacy/build/pdf.min.mjs')
        ]).then(() => {
            PDFJS.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC;
            this.PDFJSloaded = true;
            this.init();
            this.storeFacade.actions.documentViewerSetInitialized.dispatch({ initialized: true });
        });
    }

    init() {
        // Subscriptions;
        this.subscriptions.push(
            this.storeFacade.actions.documentViewerSetDocument.success$.subscribe(action => {
                const filesToDisplay = this.storeFacade.documentViewer.filesToDisplay;
                this.currentDocument.set(
                    getDocumentIndexById(filesToDisplay, action.payload.currentDocument)
                );
                this.pdfLoaded = false; // Reset until new PDF loads
                this.updateCurrentSignatureFields();
                this.activateFile(this.currentDocument());
                this.ref.markForCheck();
            }),
            this.storeFacade.actions.documentViewerSetForm.success$.subscribe(_ => {
                this.createPdfSource(true);
            }),
            this.storeFacade.actions.documentViewerSetFile.success$.subscribe(_ => {
                // Update cached signature fields when file changes
                this.updateCurrentSignatureFields();
                this.ref.markForCheck();
            }),
            this.storeFacade.actions.documentViewerSetOffline.success$.subscribe(action => {
                if (action.payload.offline) {
                    this.styles.pdfWrapper = this.staticStyles.pdfInnerFullscreenStyle;
                } else {
                    this.styles.pdfWrapper = {};
                }
                this.ref.detectChanges();
            }),
            this.storeFacade.orderApproveFailure$.subscribe(_ => {
                if (this.storeFacade.order) {
                    this.createPdfSource();
                }
            }),
            this.storeFacade.orderSignSuccess$.subscribe(oSign => this.onSignSuccess(oSign.url))
        );

        this.ref.markForCheck();
    }

    /**
     * #5580: the host is pre-checked here, not only in the route guard behind
     * `externalDocusignRedirect`. That guard cancels the navigation whether it redirects or
     * refuses, so it has no way to tell the user why nothing happened — which is exactly how
     * eSign dead-ended in silence for two months after DocuSign moved embedded signing URLs to
     * `apps*.docusign.com`. The guard keeps its own check: the route is reachable directly.
     */
    onSignSuccess(url: string) {
        if (!isAllowedDocusignRedirectUrl(url, this.environment.docusignRedirectHost)) {
            this.storeFacade.actions.documentViewerShowNotification.dispatch({
                notification: {
                    text: SIGN_REDIRECT_BLOCKED_MESSAGE,
                    status: 'error'
                }
            });
            return;
        }

        // Redirect to returned docusign url;
        this.router.navigate(routeConstants.routes.externalDocusignRedirect.index(url), {
            skipLocationChange: true
        });
    }

    getCurrentIndex(id: string) {
        const docState = this.storeFacade.documentViewer;
        return getDocumentIndexById(docState.filesToDisplay, id);
    }

    onClickNextDocument() {
        const docState = this.storeFacade.documentViewer;
        if (!docState.offline) {
            const pdf = this.pdf();
            if (pdf) {
                pdf.isLoading$.next(true);
            }

            const totalDocs = docState.filesToDisplay.length;
            let index = getDocumentIndexById(docState.filesToDisplay, docState.currentDocument);
            index++;

            if (this.pdfFullscreen) {
                this.pdfFullscreen = !this.pdfFullscreen;
            }

            if (index === totalDocs) {
                index = 0;
            }

            const order = this.storeFacade.order;
            const nextDoc = this.storeFacade.documentViewer.filesToDisplay[index];
            this.router.navigate(
                this.storeFacade.routes.documentViewer(order.id, getFileRouteId(nextDoc)),
                // #5517: preserve the ?bundle preview context across document navigation.
                { queryParamsHandling: 'preserve' }
            );
        }
    }

    onClickPrevDocument() {
        const docState = this.storeFacade.documentViewer;
        if (!docState.offline) {
            const pdf = this.pdf();
            if (pdf) {
                pdf.isLoading$.next(true);
            }

            const totalDocs = docState.filesToDisplay.length;
            let index = getDocumentIndexById(docState.filesToDisplay, docState.currentDocument);
            index--;

            if (this.pdfFullscreen) {
                this.pdfFullscreen = !this.pdfFullscreen;
            }

            if (index < 0) {
                index = totalDocs - 1;
            }

            const order = this.storeFacade.order;
            const prevDoc = this.storeFacade.documentViewer.filesToDisplay[index];
            this.router.navigate(
                this.storeFacade.routes.documentViewer(order.id, getFileRouteId(prevDoc)),
                // #5517: preserve the ?bundle preview context across document navigation.
                { queryParamsHandling: 'preserve' }
            );
        }
    }

    /**
     * Activates the file at the given file index;
     * @param fileIndex The index of the newly active file;
     */
    activateFile(fileIndex: number) {
        const order = this.storeFacade.order;
        const filesToDisplay = this.storeFacade.documentViewer.filesToDisplay;
        let offline = false;

        if (order && order.forms) {
            let currentFile = null;
            if (filesToDisplay && filesToDisplay.length > 0) {
                currentFile = filesToDisplay[fileIndex];
            }

            // If file doesn't exist at index, check for form;
            const currentForm =
                currentFile && currentFile.formId
                    ? order.forms[currentFile.formId]
                    : getFormByIndex(order, fileIndex);

            // Check for not onboarded form;
            if (currentForm && currentForm.isOnboarded === false) {
                offline = true;
                this.isPdf = offline;
            }

            if (currentFile) {
                this.isPdf = currentFile.name.toLowerCase().endsWith('.pdf');
                this.isSpreadsheet =
                    currentFile.name.toLowerCase().endsWith('.csv') ||
                    currentFile.name.toLowerCase().endsWith('.xlsx');
            }

            if (currentFile || currentForm) {
                if (this.isPdf) {
                    this.createPdfSource(offline);
                } else if (this.isSpreadsheet) {
                    console.log('Download in order to view file.');
                } else {
                    this.createImageSource();
                }
            }
        }
        this.ref.markForCheck();
    }

    createImageSource() {
        this.loadingImage.next(true);

        const order = this.storeFacade.order;
        const docState = this.storeFacade.documentViewer;
        const filesToDisplay = docState.filesToDisplay;
        this.pdfLoaded = false;
        // Same -1 index as createPdfSource: fall back to the file the store already resolved
        // rather than dereferencing `undefined`. refs trade-platform/trade-platform#7593
        const file = filesToDisplay[this.currentDocument()] ?? docState.currentFile;
        if (!file) {
            return;
        }

        this.pdfSource = {
            url: `${this.storeFacade.apiUrl}/${order.id}/download/${
                file.id
            }?download=false&cacheBuster=${new Date().getTime()}`
        };
        this.ref.markForCheck();
    }

    createPdfSource(offline = false) {
        const order = this.storeFacade.order;
        const docState = this.storeFacade.documentViewer;
        const filesToDisplay = docState.filesToDisplay;
        const currentFile = docState.currentFile;
        const currentForm = docState.currentForm;
        let url = '';

        if (currentForm && (docState.offline || offline)) {
            const formId = currentForm.id;
            url = `${
                this.environment.adminApi
            }forms/${formId}/download/template?cacheBuster=${new Date().getTime()}`;
        }

        if (currentFile && order) {
            /*
              `currentDocument()` is an index into `filesToDisplay` and is -1 when the requested
              document is no longer on the order, which used to dereference `undefined` here.
              Fall back to the file the store already resolved.
              refs trade-platform/trade-platform#7593
            */
            const file = filesToDisplay[this.currentDocument()] ?? currentFile;
            url = `${this.storeFacade.apiUrl}/${order.id}/download/${
                file.id
            }?download=false&cacheBuster=${new Date().getTime()}`;
        }

        if (url) {
            const pdfSource: PDFSource = { url };

            const currentToken = 'Bearer ' + (getFromStorage('auth') as Auth).accessToken;
            pdfSource.httpHeaders = {
                Authorization: currentToken,
                'Accept-Content-Type': 'application/pdf',
                'Cache-Control': 'no-store'
            };

            this.pdfSource = pdfSource;
            this.ref.markForCheck();
        }
    }

    canDisplayPagination() {
        const pdf = this.pdf();
        return (
            pdf &&
            this.isPdf &&
            Number.parseFloat(pdf.page() as any) >= 0 &&
            Number.parseFloat(pdf.totalPages as any) >= 0
        );
    }

    onLoadImage() {
        this.loadingImage.next(false);
        this.pdfLoaded = true;
        this.pdfLoadError = false;
        this.updateCurrentSignatureFields();
    }

    onLoadImageError() {
        this.loadingImage.next(false);
        this.pdfLoaded = true;
        this.pdfLoadError = true;
    }

    onLoadDocument() {
        this.loadingImage.next(false);
        this.pdfLoaded = true;
        this.pdfLoadError = false;
        this.updateCurrentSignatureFields();
        this.ref.detectChanges(); // Force immediate re-render for signature overlay

        // Handle pending signature navigation after PDF loads
        if (this.pendingSignatureNavigation) {
            // Small delay to ensure PDF is fully rendered
            setTimeout(() => {
                if (this.pendingSignatureNavigation) {
                    this.scrollToSignature(this.pendingSignatureNavigation);
                    this.pendingSignatureNavigation = null;
                }
            }, 100);
        }
    }

    onLoadDocumentError(error: any) {
        this.pdfLoaded = true;
        this.pdfLoadError = true;
        this.pendingSignatureNavigation = null;
        this.updateCurrentSignatureFields();

        if (error && (error.status === 401 || error.status === 403)) {
            this.authService.callLogout(true);
        }
    }

    onClickFullscreen() {
        const docState = this.storeFacade.documentViewer;
        this.pdfFullscreen = !this.pdfFullscreen;

        if (this.pdfFullscreen) {
            this.styles.pdfWrapper = this.staticStyles.pdfFullScreenStyle;
        } else {
            this.styles.pdfWrapper = docState.offline
                ? this.staticStyles.pdfInnerFullscreenStyle
                : {};
        }

        this.fitDocument();
    }

    onClickRotate() {
        if (this.isPdf) {
            this.pdf()?.rotate();
        } else {
            this.image()?.rotate();
        }
    }

    zoomIn() {
        if (this.isPdf) {
            this.pdf()?.zoomIn();
        } else {
            this.image()?.zoomIn();
        }
    }

    zoomOut() {
        if (this.isPdf) {
            this.pdf()?.zoomOut();
        } else {
            this.image()?.zoomOut();
        }
    }

    fitDocument() {
        // Wait for parent container to go fullscreen before attempting to fit PDF into it;
        setTimeout(() => {
            this.doFit();
        }, 100);
    }

    doFit() {
        const pdf = this.pdf();
        if (pdf) {
            pdf.fit();
            pdf.simulateScroll(); // Trigger PDF resize;
        } else {
            this.image()?.fit();
        }
    }

    @HostListener('window:resize', ['$event'])
    @debounceListener()
    scroll() {
        this.doFit();
    }

    goToPage(page: number) {
        this.pdf()?.goToPage(page);
    }

    scrollToSignature(field: SignatureField) {
        this.pdf()?.scrollToSignature(field);
    }

    setPendingSignatureNavigation(field: SignatureField) {
        this.pendingSignatureNavigation = field;
    }

    ngOnChanges(changes: SimpleChanges) {
        // The signatures preview is fetched on a separate async timeline from the PDF (a standalone
        // request in the parent's loadSignaturesPreview). When it resolves after the PDF has already
        // loaded, no file/load event fires to refresh the overlay cache — so refresh it here. The
        // pdfLoaded guard inside updateCurrentSignatureFields still suppresses pre-render painting.
        if (changes['signaturesPreview']) {
            this.updateCurrentSignatureFields();
            this.ref.markForCheck();
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }
}
